import { Brain, Headphones, Folder, Smartphone } from "lucide-react";

export const LandingFeatures = () => {
  const features = [
    {
      icon: Brain,
      title: "Memória Aprimorada",
      description:
        "A KopfAI armazena os pontos-chave e contextos de conversas anteriores, detectando padrões e eliminando a necessidade de repetição.",
    },
    {
      icon: Headphones,
      title: "Fale e ouça",
      description:
        "Sem energia para digitar? Envie uma mensagem de voz. Cansado de ler? Dê play e escute as respostas da IA.",
    },
    {
      icon: Folder,
      title: "Organize suas conversas",
      description: "Crie chats por dia como um diário pessoal. Organize seus pensamentos e volte sempre que precisar.",
    },
    {
      icon: Smartphone,
      title: "Acesse de Onde Estiver",
      description: "Continue sua conversa de forma fluida no celular, tablet ou computador. Sincronização instantânea.",
    },
  ];

  return (
    <section className="relative w-full min-h-[789px] py-16 md:py-24 px-6 md:px-12 overflow-hidden">
      {/* Aqueous Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-[#FFD166]/5 to-white -z-10"></div>

      {/* Animated Blobs */}
      <div
        className="absolute top-20 left-[10%] w-[500px] h-[500px] rounded-full opacity-10 blur-3xl -z-10"
        style={{
          background: "radial-gradient(circle, rgba(74, 78, 105, 0.4) 0%, rgba(74, 78, 105, 0) 70%)",
          animation: "float 20s ease-in-out infinite",
        }}
      ></div>
      <div
        className="absolute bottom-20 right-[10%] w-[600px] h-[600px] rounded-full opacity-12 blur-3xl -z-10"
        style={{
          background: "radial-gradient(circle, rgba(255, 209, 102, 0.5) 0%, rgba(255, 209, 102, 0) 70%)",
          animation: "float 28s ease-in-out infinite reverse",
        }}
      ></div>

      {/* Header */}
      <div
        className="text-center mb-16"
        style={{
          animation: "fadeInUp 0.8s ease-out",
        }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-[#4A4E69] mb-4">Feito para se encaixar na sua vida</h2>
        <p className="text-base md:text-lg text-[#272D37]/70 max-w-3xl mx-auto">
          Recursos de tecnologia e conveniência que tornam seu autocuidado mais fácil.
        </p>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 justify-items-center max-w-6xl mx-auto mb-16">
        {features.map((feature, index) => (
          <div
            key={index}
            className="group flex flex-col items-center text-center w-full max-w-[280px] transition-transform duration-500 hover:-translate-y-2"
            style={{
              animation: `fadeInUp 0.8s ease-out ${0.2 + index * 0.1}s backwards`,
            }}
          >
            {/* Icon Container with Glow */}
            <div className="relative mb-6">
              {/* Glow effect */}
              <div
                className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-10"
                style={{
                  background: "radial-gradient(circle, rgba(255, 209, 102, 0.6), transparent 70%)",
                  transform: "scale(1.5)",
                }}
              ></div>

              <div
                className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FFD166] to-[#FFD166]/70 flex items-center justify-center shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                style={{
                  boxShadow: "0 10px 30px rgba(255, 209, 102, 0.25)",
                }}
              >
                <feature.icon className="w-10 h-10 text-[#4A4E69] transition-transform duration-500 group-hover:scale-110" />
              </div>
            </div>

            <h3 className="text-xl font-bold text-[#4A4E69] mb-3 transition-colors duration-300 group-hover:text-[#272D37]">
              {feature.title}
            </h3>
            <p className="text-base text-[#272D37]/75 leading-relaxed">{feature.description}</p>
          </div>
        ))}
      </div>

      {/* Bottom Section with Glassmorphic Card */}
      <div
        className="border-t border-[#D8DEE9]/40 pt-12 max-w-4xl mx-auto"
        style={{
          animation: "fadeInUp 0.8s ease-out 0.8s backwards",
        }}
      >
        <div className="relative rounded-3xl bg-white/50 backdrop-blur-xl border border-[#D8DEE9]/50 p-8 md:p-10 shadow-xl overflow-hidden group transition-all duration-500 hover:shadow-2xl">
          {/* Subtle gradient overlay */}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 -z-10"
            style={{
              background: "radial-gradient(circle at 50% 0%, rgba(255, 209, 102, 0.08), transparent 60%)",
            }}
          ></div>

          {/* Decorative elements */}
          <div className="absolute top-4 right-4 w-16 h-16 rounded-full bg-[#FFD166]/10 blur-2xl"></div>
          <div className="absolute bottom-4 left-4 w-20 h-20 rounded-full bg-[#4A4E69]/5 blur-2xl"></div>

          <div className="relative z-10">
            <h3 className="text-xl font-bold text-[#4A4E69] text-center mb-4 flex items-center justify-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-[#FFD166] animate-pulse"></span>
              Sua assistente está sempre evoluindo.
            </h3>
            <p className="text-base text-[#272D37]/80 text-center leading-relaxed">
              Estamos constantemente atualizando a KopfAI. Desenvolvemos novos treinamentos e ferramentas em colaboração
              com especialistas constantemente e testamos seu impacto antes do lançamento. Assim, garantimos que cada
              conversa ofereça apoio confiável, baseado em evidências.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
          }
          50% { 
            transform: translate(40px, 40px) scale(1.12); 
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};
